import React, { forwardRef } from 'react';

import { cn } from '@/utils/cn';

export interface InputProps extends React.ComponentPropsWithoutRef<'input'> {
  label?: string;
  suffix?: string;
  rightSide?: React.ReactNode;
  leftSide?: React.ReactNode;
  isVisible?: boolean;
  parentClassName?: string;
  onPressEnter?: () => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, rightSide, leftSide, id, isVisible = true, className, parentClassName, onPressEnter, ...rest }, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && onPressEnter) {
        onPressEnter();
      }
    };

    return (
      <div
        className={cn(
          'relative bg-gray-50 md:h-12 h-10 border rounded-lg w-full px-4 gap-2 flex items-center justify-between max-w-full',
          {
            hidden: !isVisible,
          },
          parentClassName,
          rest.value ? 'bg-white border-gray-300' : 'bg-gray-100 border-transparent',
        )}
      >
        {leftSide && <span className="grid place-items-center">{leftSide}</span>}
        {label && (
          <label htmlFor={id} className="text-sm text-gray-700">
            {label}
          </label>
        )}
        <input
          className={cn('group-hover:bg-gray-200 bg-transparent sm:text-base text-sm flex-1 outline-none', className, {
            'bg-white': rest.value,
          })}
          id={id}
          ref={ref}
          onKeyDown={handleKeyDown}
          {...rest}
        />
        {rightSide && <span className="grid place-items-center">{rightSide}</span>}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
